/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The composer recipe-tag rules, as pure functions so they can be tested
 * without a DOM (the house pattern: node --test over pure logic, like
 * lib/dates.test.ts). The components stay thin over these.
 *
 * A tag points at a recipe ALREADY SHARED with this circle through library
 * sharing (029) — the picker is fed by the same ?includeOwn=true shelf as the
 * SharedRecipeStrip, which is why the §6 fix and this feature are one change.
 */

import type { AvailableLibraryRecipe } from "../../api/types.ts";

/**
 * The picker's two sections. "Shared by the group" (other members' shares)
 * first — they are the actionable ones — "Shared by you" second. When the
 * author's own library sharing is OFF there is no own section at all: the
 * composer shows the share-your-library prompt instead, rather than inventing
 * a quiet per-recipe share that 029 doesn't have.
 */
export interface TagPickerShelf {
  group: AvailableLibraryRecipe[];
  own: AvailableLibraryRecipe[];
  ownSharingOn: boolean;
}

export function tagPickerShelf(
  recipes: AvailableLibraryRecipe[],
  ownSharingOn: boolean,
): TagPickerShelf {
  return {
    group: recipes.filter((r) => !r.isOwn),
    own: ownSharingOn ? recipes.filter((r) => r.isOwn) : [],
    ownSharingOn,
  };
}

/**
 * One dish identity per post. A selected tag wins over the free-text dish
 * field: the post carries recipeId and NOT suggestedTitle. (The server would
 * accept both; the UI steers to one or the other.)
 */
export function tagPostFields(
  tag: AvailableLibraryRecipe | null,
  dish: string,
): { recipeId?: string; suggestedTitle?: string } {
  if (tag) return { recipeId: tag.recipeId };
  const title = dish.trim();
  return title ? { suggestedTitle: title } : {};
}

/**
 * The Post button's rule, mirroring the server (S-2): a photo alone is a
 * complete message, but a TAG alone is not — a tag is an attachment, never a
 * substitute for words or a photo.
 */
export function canSendPost(body: string, photoCount: number): boolean {
  return body.trim().length > 0 || photoCount > 0;
}

/** Where tapping a tag on a post takes THIS reader. Null means: not a link. */
export function recipeRefDestination(ref: {
  status: "yours" | "accepted" | "available" | "unavailable";
  localRecipeId?: string;
}): string | null {
  if ((ref.status === "yours" || ref.status === "accepted") && ref.localRecipeId) {
    return `/recipes/${ref.localRecipeId}`;
  }
  // available → preview dialog, not a navigation; unavailable → plain text.
  return null;
}
